import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast.service';
import { CreateOrUpdateWaterService, WaterService, WaterServicesService } from '@services/water-services.service';
import { WaterBasic, WatersService } from '@services/waters.service';
import { catchError, firstValueFrom, forkJoin, map, of, switchMap } from 'rxjs';

type Locale = 'Nl' | 'En' | 'De' | 'It' | 'Fr';

@Component({
  selector: 'app-water-service-form',
  templateUrl: './water-service-form.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    MatBadgeModule,
    MatSelectModule,
    DragDropModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaterServiceFormComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly watersService = inject(WatersService);
  private readonly waterServicesService = inject(WaterServicesService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private waterId?: number;
  readonly water = signal<WaterBasic | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly activeLocale = signal<Locale>('Nl');
  readonly formTouched = signal(false);

  readonly locales: { code: Locale; label: string }[] = [
    { code: 'Nl', label: 'NL' },
    { code: 'En', label: 'EN' },
    { code: 'De', label: 'DE' },
    { code: 'It', label: 'IT' },
    { code: 'Fr', label: 'FR' },
  ];

  readonly activeLocaleIndex = computed(() => {
    return this.locales.findIndex((l) => l.code === this.activeLocale());
  });

  readonly activeLocaleLabel = computed(() => {
    return this.locales[this.activeLocaleIndex()]?.label || 'Nl';
  });

  readonly form = this.fb.group({
    services: this.fb.array([]),
  });

  ngOnInit() {
    this.route.params
      .pipe(
        switchMap((params) => {
          this.waterId = +params['waterId'];
          this.loading.set(true);

          return forkJoin({
            services: this.waterServicesService.getByWater(this.waterId).pipe(
              map((data) => ({ error: false, data })),
              catchError(() => of({ error: true, data: [] })),
            ),
            water: this.watersService.getWater(this.waterId).pipe(
              map((data) => ({ error: false, data })),
              catchError(() => of({ error: true, data: null })),
            ),
          });
        }),
      )
      .subscribe({
        next: ({ services, water }) => {
          this.loading.set(false);

          if (services.error) {
            this.toastService.showError(this.translateService.instant('water-services.toast.load-failed'));
            this.router.navigate(['/water-services']);
            return;
          }

          if (water.error || !water.data) {
            this.toastService.showError(this.translateService.instant('water-services.toast.not-found'));
            this.router.navigate(['/water-services']);
            return;
          }

          this.water.set(water.data);

          if (services.data.length > 0) {
            for (const service of services.data) {
              this.addServiceFromData(service);
            }
          } else {
            this.addService();
          }
        },
        error: (error) => {
          console.error('Error loading water service data:', error);
          this.toastService.showError(this.translateService.instant('water-services.toast.load-failed'));
          this.router.navigate(['/water-services']);
          this.loading.set(false);
        },
      });
  }

  get services(): FormArray {
    return this.form.get('services') as FormArray;
  }

  private createServiceFormGroup(service?: WaterService): FormGroup {
    return this.fb.group({
      id: [service?.id || 0],
      titleNl: [service?.titleNl || '', [Validators.maxLength(200)]],
      titleEn: [service?.titleEn || '', [Validators.required, Validators.maxLength(200)]],
      titleDe: [service?.titleDe || '', [Validators.maxLength(200)]],
      titleIt: [service?.titleIt || '', [Validators.maxLength(200)]],
      titleFr: [service?.titleFr || '', [Validators.maxLength(200)]],
      unitNl: [service?.unitNl || '', [Validators.maxLength(50)]],
      unitEn: [service?.unitEn || '', [Validators.required, Validators.maxLength(50)]],
      unitDe: [service?.unitDe || '', [Validators.maxLength(50)]],
      unitIt: [service?.unitIt || '', [Validators.maxLength(50)]],
      unitFr: [service?.unitFr || '', [Validators.maxLength(50)]],
      pricePerUnit: [service?.pricePerUnit || 0, [Validators.required, Validators.min(0)]],
      image: [service?.image || null],
      imageFile: [null],
      displayOrder: [service?.displayOrder || this.services.length],
      imagePreview: [null],
      imageUrl: [service?.imageUrl],
    });
  }

  addService() {
    this.services.push(this.createServiceFormGroup());
  }

  private addServiceFromData(service: WaterService) {
    this.services.push(this.createServiceFormGroup(service));
  }

  removeService(index: number) {
    this.services.removeAt(index);
    this.updateDisplayOrders();
  }

  drop(event: CdkDragDrop<FormGroup[]>) {
    const servicesArray = this.services.controls as FormGroup[];
    moveItemInArray(servicesArray, event.previousIndex, event.currentIndex);
    this.services.controls = servicesArray;
    this.updateDisplayOrders();
  }

  private updateDisplayOrders() {
    this.services.controls.forEach((control, index) => {
      control.patchValue({ displayOrder: index });
    });
  }

  onFileSelected(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.toastService.showError(this.translateService.instant('water-services.form.image-invalid'));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.toastService.showError(this.translateService.instant('water-services.form.image-size'));
        return;
      }

      const serviceGroup = this.services.at(index) as FormGroup;
      serviceGroup.patchValue({ imageFile: file, imageUrl: null });

      // Create preview
      const reader = new FileReader();
      reader.addEventListener('load', (e: ProgressEvent<FileReader>) => {
        if (e.target?.result) {
          serviceGroup.patchValue({ imagePreview: e.target.result });
          this.cdr.detectChanges();
        }
      });
      reader.readAsDataURL(file);
    }
  }

  removeImage(index: number) {
    const serviceGroup = this.services.at(index) as FormGroup;
    serviceGroup.patchValue({
      image: null,
      imageFile: null,
      imagePreview: null,
      imageUrl: null,
    });
  }

  asFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  getImageUrl(service: FormGroup): string | null {
    const imagePreview = service.get('imagePreview')?.value;
    if (imagePreview) {
      return imagePreview;
    }

    const imageUrl = service.get('imageUrl')?.value;
    if (imageUrl) {
      return imageUrl;
    }

    return null;
  }

  getTitleControlName(locale: Locale): string {
    return `title${locale}`;
  }

  getUnitControlName(locale: Locale): string {
    return `unit${locale}`;
  }

  hasLocaleErrors(locale: Locale): boolean {
    if (!this.formTouched()) return false;

    const titleControl = this.getTitleControlName(locale);
    const unitControl = this.getUnitControlName(locale);

    return this.services.controls.some((control) => {
      const title = control.get(titleControl);
      const unit = control.get(unitControl);
      return title?.invalid || unit?.invalid;
    });
  }

  getNlReference(service: FormGroup, locale: Locale): string {
    if (locale === 'Nl') return '';
    const nlTitle = service.get(this.getTitleControlName('Nl'))?.value;
    return nlTitle ? `(${nlTitle})` : '';
  }

  getNlUnitReference(service: FormGroup, locale: Locale): string {
    if (locale === 'Nl') return '';
    const nlUnit = service.get(this.getUnitControlName('Nl'))?.value;
    return nlUnit ? `(${nlUnit})` : '';
  }

  hasLocaleEmptyValues(service: FormGroup, locale: Locale): boolean {
    const titleControl = service.get(this.getTitleControlName(locale));
    const unitControl = service.get(this.getUnitControlName(locale));
    const titleValue = titleControl?.value || '';
    const unitValue = unitControl?.value || '';
    return titleValue.trim() === '' || unitValue.trim() === '';
  }

  async save(): Promise<void> {
    this.formTouched.set(true);
    const waterId = this.waterId;
    if (!waterId) return;

    if (this.form.invalid) {
      this.toastService.showError(this.translateService.instant('water-services.form.fill-required'));
      return;
    }

    this.saving.set(true);

    try {
      // Upload new images first

      for (let i = 0; i < this.services.length; i++) {
        const serviceGroup = this.services.at(i) as FormGroup;
        const imageFile = serviceGroup.get('imageFile')?.value;

        if (imageFile) {
          const response = await firstValueFrom(this.waterServicesService.uploadImage(imageFile));
          if (response) {
            serviceGroup.patchValue({ image: response.filename, imageFile: null });
          }
        }
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      this.toastService.showError(this.translateService.instant('water-services.form.upload-failed'));
      this.saving.set(false);
      return;
    }

    try {
      // Prepare services data
      const services: CreateOrUpdateWaterService[] = this.services.controls.map((control) => {
        const value = control.value;
        return {
          id: value.id,
          titleNl: value.titleNl,
          titleEn: value.titleEn,
          titleDe: value.titleDe,
          titleIt: value.titleIt,
          titleFr: value.titleFr,
          unitNl: value.unitNl,
          unitEn: value.unitEn,
          unitDe: value.unitDe,
          unitIt: value.unitIt,
          unitFr: value.unitFr,
          pricePerUnit: value.pricePerUnit,
          image: value.image,
          displayOrder: value.displayOrder,
        };
      });

      // Bulk update
      await firstValueFrom(this.waterServicesService.bulkUpdateByWater(waterId, services));

      this.toastService.showSuccess(this.translateService.instant('water-services.form.save-success'));
      this.router.navigate(['/water-services']);
    } catch (error) {
      console.error('Error saving water services:', error);
      this.toastService.showError(this.translateService.instant('water-services.form.save-failed'));
    } finally {
      this.saving.set(false);
    }
  }

  cancel() {
    this.router.navigate(['/water-services']);
  }

  readonly translating = signal(false);

  async translateMissing(): Promise<void> {
    const sourceLocale = 'Nl';
    const targetLocales = ['En', 'De', 'It', 'Fr'] as Locale[];

    this.translating.set(true);

    try {
      // Build batch request with only items that need translation
      const items: { title: string; unit: string; to: string[] }[] = [];
      const serviceIndexMap: number[] = []; // Track which service each item belongs to

      this.services.controls.forEach((control) => {
        const sourceTitle = control.get(this.getTitleControlName(sourceLocale))?.value || '';
        const sourceUnit = control.get(this.getUnitControlName(sourceLocale))?.value || '';

        // Skip if source NL doesn't have both title and unit
        if (!sourceTitle.trim() || !sourceUnit.trim()) {
          return;
        }

        // Determine which target languages need translation
        const neededLanguages = targetLocales.filter((targetLang) => {
          const targetTitle = control.get(this.getTitleControlName(targetLang))?.value || '';
          const targetUnit = control.get(this.getUnitControlName(targetLang))?.value || '';
          // Need translation if either title or unit is empty
          return !targetTitle.trim() || !targetUnit.trim();
        });

        // Only add to batch if at least one language needs translation
        if (neededLanguages.length > 0) {
          items.push({
            title: sourceTitle,
            unit: sourceUnit,
            to: neededLanguages.map((l) => l.toLowerCase()),
          });
          serviceIndexMap.push(this.services.controls.indexOf(control));
        }
      });

      // Check if there's anything to translate
      if (items.length === 0) {
        this.toastService.showError(this.translateService.instant('water-services.form.translate-no-missing'));
        return;
      }

      const response = await firstValueFrom(
        this.waterServicesService.translateBatch({
          from: sourceLocale.toLowerCase(),
          items,
        }),
      );

      if (response.success && response.items) {
        // Update form with translated values
        response.items.forEach((itemResult, itemIndex) => {
          const serviceIndex = serviceIndexMap[itemIndex];
          const serviceGroup = this.services.at(serviceIndex) as FormGroup;

          if (serviceGroup && itemResult.translations && Object.keys(itemResult.translations).length > 0) {
            const updates: Record<string, string> = {};

            // Apply translations for each language
            Object.entries(itemResult.translations).forEach(([lang, fields]) => {
              const targetLocale = this.capitalizeFirst(lang) as Locale;

              // Only update title if target field is empty
              const titleControl = serviceGroup.get(this.getTitleControlName(targetLocale));
              const currentTitle = titleControl?.value || '';
              if (!currentTitle.trim()) {
                updates[this.getTitleControlName(targetLocale)] = fields.title;
              }

              // Only update unit if target field is empty
              const unitControl = serviceGroup.get(this.getUnitControlName(targetLocale));
              const currentUnit = unitControl?.value || '';
              if (!currentUnit.trim()) {
                updates[this.getUnitControlName(targetLocale)] = fields.unit;
              }
            });

            // Apply updates
            if (Object.keys(updates).length > 0) {
              serviceGroup.patchValue(updates);
            }
          }
        });

        this.toastService.showSuccess(this.translateService.instant('water-services.form.translate-success'));
        this.cdr.detectChanges();
      } else {
        this.toastService.showError(
          response.message || this.translateService.instant('water-services.form.translate-failed'),
        );
      }
    } catch (error) {
      console.error('Translation error:', error);
      this.toastService.showError(this.translateService.instant('water-services.form.translate-failed'));
    } finally {
      this.translating.set(false);
    }
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
